// WorkOrders — Sprint 11 orchestrator. Now a thin layout shell:
//  * data + persistence  → useWorkOrdersData
//  * presentation        → WorkOrdersStatsRow + WorkOrdersStatusFilter
//                           + WorkOrdersTable
//  * 5 modals            → WorkOrderModals
// The orchestrator only owns transient UI state (which modal is open,
// which row is being edited/completed/assigned, AntD Form instances).
import React, { useMemo, useState } from 'react';
import { Button, Form, Modal } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuthStore } from '../store/authStore';
import { useWorkOrdersData } from './workOrders/useWorkOrdersData';
import { WorkOrdersStatsRow } from './workOrders/WorkOrdersStatsRow';
import { WorkOrdersStatusFilter } from './workOrders/WorkOrdersStatusFilter';
import { WorkOrdersTable } from './workOrders/WorkOrdersTable';
import {
  AssignVendorModal, CompleteOrderModal, SignOffModal,
  UpdateCostsModal, WorkOrderEditModal,
} from './workOrders/WorkOrderModals';
import type { StatusFilter, WorkOrder } from './workOrders/workOrderTypes';

export const WorkOrders: React.FC = () => {
  const { currentCondo } = useAuthStore();

  const {
    workOrders, vendors,
    saveOrder, completeOrder, changeStatus, deleteOrder,
    assignVendor, updateCosts, signOff,
  } = useWorkOrdersData({ condoId: currentCondo?.id });

  // ── Transient UI state ──────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  const [editForm]      = Form.useForm();
  const [completeForm]  = Form.useForm();
  const [vendorForm]    = Form.useForm();
  const [costForm]      = Form.useForm();
  const [signOffForm]   = Form.useForm();

  const [editOpen,     setEditOpen]     = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [vendorOpen,   setVendorOpen]   = useState(false);
  const [costOpen,     setCostOpen]     = useState(false);
  const [signOffOpen,  setSignOffOpen]  = useState(false);

  const [editingOrder,    setEditingOrder]    = useState<WorkOrder | null>(null);
  const [completingOrder, setCompletingOrder] = useState<WorkOrder | null>(null);
  const [assigningOrder,  setAssigningOrder]  = useState<WorkOrder | null>(null);
  const [costingOrder,    setCostingOrder]    = useState<WorkOrder | null>(null);
  const [signingOrder,    setSigningOrder]    = useState<WorkOrder | null>(null);

  // ── Derived data ────────────────────────────────────────────────────
  const filteredOrders = useMemo(() =>
    statusFilter === 'ALL'
      ? workOrders
      : workOrders.filter((o: WorkOrder) => o.status === statusFilter),
    [workOrders, statusFilter],
  );

  const stats = useMemo(() => ({
    open:       workOrders.filter((o: WorkOrder) => o.status === 'OPEN').length,
    inProgress: workOrders.filter((o: WorkOrder) => o.status === 'IN_PROGRESS').length,
    completed:  workOrders.filter((o: WorkOrder) => o.status === 'COMPLETED').length,
    total:      workOrders.length,
  }), [workOrders]);

  // ── Modal openers (preset form values for the row being acted on) ───

  const openCreate = () => {
    setEditingOrder(null);
    editForm.resetFields();
    setEditOpen(true);
  };

  const openEdit = (order: WorkOrder) => {
    setEditingOrder(order);
    editForm.setFieldsValue({
      title:         order.title,
      description:   order.description,
      unitNumber:    order.unitNumber,
      vendorName:    order.vendorName,
      vendorContact: order.vendorContact,
      estimatedCost: order.estimatedCost,
      scheduledDate: order.scheduledDate ? dayjs(order.scheduledDate) : null,
      status:        order.status,
      priority:      order.priority,
      notes:         order.notes,
    });
    setEditOpen(true);
  };

  const openComplete = (order: WorkOrder) => {
    setCompletingOrder(order);
    completeForm.resetFields();
    setCompleteOpen(true);
  };

  const openVendor = (order: WorkOrder) => {
    setAssigningOrder(order);
    vendorForm.resetFields();
    vendorForm.setFieldsValue({
      vendorName:    (order as any).vendorName,
      vendorContact: (order as any).vendorContact,
    });
    setVendorOpen(true);
  };

  const openCosts = (order: WorkOrder) => {
    setCostingOrder(order);
    costForm.resetFields();
    costForm.setFieldsValue({
      laborCost:     (order as any).laborCost,
      materialCost:  (order as any).materialCost,
      invoiceNumber: (order as any).invoiceNumber,
    });
    setCostOpen(true);
  };

  const openSignOff = (order: WorkOrder) => {
    setSigningOrder(order);
    signOffForm.resetFields();
    setSignOffOpen(true);
  };

  // ── Submit handlers ─────────────────────────────────────────────────

  const handleEditSubmit = async (values: any) => {
    try {
      await saveOrder(
        {
          condoId:       currentCondo?.id || '',
          title:         values.title,
          description:   values.description,
          unitNumber:    values.unitNumber,
          vendorName:    values.vendorName,
          vendorContact: values.vendorContact,
          estimatedCost: values.estimatedCost,
          scheduledDate: values.scheduledDate ? values.scheduledDate.format('YYYY-MM-DD') : undefined,
          status:        values.status ?? 'OPEN',
          priority:      values.priority ?? 'MEDIUM',
          notes:         values.notes,
        },
        editingOrder?.id,
      );
      setEditOpen(false);
      editForm.resetFields();
    } catch { /* hook already showed the toast; keep modal open */ }
  };

  const handleCompleteSubmit = async (values: any) => {
    if (!completingOrder) return;
    try {
      await completeOrder(completingOrder.id, values.actualCost);
      setCompleteOpen(false);
      completeForm.resetFields();
    } catch { /* keep modal open on error */ }
  };

  const handleAssignVendor = async () => {
    try {
      const values = await vendorForm.validateFields();
      const vendor = (vendors || []).find((v: any) => v.id === values.vendorId);
      await assignVendor(
        assigningOrder!.id,
        values.vendorId || '',
        vendor?.name  || values.vendorName    || '',
        vendor?.phone || values.vendorContact || '',
      );
      setVendorOpen(false);
    } catch { /* validation errors are inline */ }
  };

  const handleUpdateCosts = async () => {
    try {
      const values = await costForm.validateFields();
      await updateCosts(
        costingOrder!.id,
        values.laborCost    || 0,
        values.materialCost || 0,
        values.invoiceNumber,
      );
      setCostOpen(false);
    } catch { /* validation errors are inline */ }
  };

  const handleSignOff = async () => {
    try {
      const values = await signOffForm.validateFields();
      await signOff(signingOrder!.id, values.signedOffBy);
      setSignOffOpen(false);
    } catch { /* validation errors are inline */ }
  };

  const handleDelete = (order: WorkOrder) => {
    Modal.confirm({
      title:    'Delete Work Order?',
      icon:     <ExclamationCircleOutlined />,
      content:  `Are you sure you want to delete "${order.title}"? This action cannot be undone.`,
      okText:   'Yes, Delete',
      okType:   'danger',
      cancelText: 'Cancel',
      onOk:     () => deleteOrder(order.id),
    });
  };

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Work Orders</h1>
          <p className="text-gray-600">Manage vendor work orders and track completion</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          New Work Order
        </Button>
      </div>

      <WorkOrdersStatsRow {...stats} />
      <WorkOrdersStatusFilter value={statusFilter} onChange={setStatusFilter} />

      <WorkOrdersTable
        rows={filteredOrders}
        onStart={(o)        => changeStatus(o, 'IN_PROGRESS')}
        onComplete={openComplete}
        onCancel={(o)       => changeStatus(o, 'CANCELLED')}
        onAssignVendor={openVendor}
        onUpdateCosts={openCosts}
        onSignOff={openSignOff}
        onEdit={openEdit}
        onDelete={handleDelete}
      />

      {/* Modals */}
      <WorkOrderEditModal
        open={editOpen}
        editingOrder={editingOrder}
        form={editForm}
        onCancel={() => { setEditOpen(false); editForm.resetFields(); }}
        onSubmit={handleEditSubmit}
      />
      <CompleteOrderModal
        open={completeOpen}
        completingOrder={completingOrder}
        form={completeForm}
        onCancel={() => { setCompleteOpen(false); completeForm.resetFields(); }}
        onSubmit={handleCompleteSubmit}
      />
      <AssignVendorModal
        open={vendorOpen}
        form={vendorForm}
        vendors={vendors || []}
        onCancel={() => setVendorOpen(false)}
        onOk={handleAssignVendor}
      />
      <UpdateCostsModal
        open={costOpen}
        form={costForm}
        onCancel={() => setCostOpen(false)}
        onOk={handleUpdateCosts}
      />
      <SignOffModal
        open={signOffOpen}
        signingOrder={signingOrder}
        form={signOffForm}
        onCancel={() => setSignOffOpen(false)}
        onOk={handleSignOff}
      />
    </div>
  );
};

export default WorkOrders;
